import type { CustomQuestion, KnownQuestion } from "./CallScriptManager";
import { customizeKnownQuestionWithProcedureCode } from "./customizeProcedureCodeQuestion";
import type { CallScriptTemplate, CustomTemplateQuestion } from "./callScriptTemplates";

export interface ApplyCallScriptTemplateResult {
  selectedQuestionIds: Set<string>;
  orderedQuestionIds: string[];
  customQuestions: CustomQuestion[];
  templateProcedureQuestionIds: string[];
}

function matchesKnownQuestion(
  question: KnownQuestion,
  matcher: CallScriptTemplate["knownQuestionMatchers"][number],
): boolean {
  if (question.results_field_path !== matcher.results_field_path) {
    return false;
  }
  const matcherCode = matcher.procedure_code ?? null;
  const questionCode = question.procedure_code ?? null;
  return matcherCode === questionCode;
}

function toCustomQuestion(def: CustomTemplateQuestion): CustomQuestion {
  return {
    id: crypto.randomUUID(),
    question_text: def.question_text,
    category: def.category,
    expected_response_type: def.expected_response_type,
    results_field_path: def.results_field_path ?? null,
    procedure_code: def.procedure_code ?? null,
    is_custom: true,
  };
}

export function applyCallScriptTemplate(
  template: CallScriptTemplate,
  knownQuestions: KnownQuestion[],
  procedureCode: string,
): ApplyCallScriptTemplateResult {
  const selectedQuestionIds = new Set<string>();
  const orderedQuestionIds: string[] = [];
  const customQuestions: CustomQuestion[] = [];
  const templateProcedureQuestionIds: string[] = [];

  for (const matcher of template.knownQuestionMatchers) {
    const match = knownQuestions.find((q) => matchesKnownQuestion(q, matcher));
    if (!match) {
      continue;
    }

    if (matcher.procedure_code === "XYZ") {
      const customized = customizeKnownQuestionWithProcedureCode(match, procedureCode);
      customQuestions.push(customized);
      orderedQuestionIds.push(customized.id);
      templateProcedureQuestionIds.push(customized.id);
    } else {
      selectedQuestionIds.add(match.id);
      orderedQuestionIds.push(match.id);
    }
  }

  for (const def of template.customQuestions ?? []) {
    const customQuestion = toCustomQuestion(def);
    customQuestions.push(customQuestion);
    orderedQuestionIds.push(customQuestion.id);
  }

  return {
    selectedQuestionIds,
    orderedQuestionIds,
    customQuestions,
    templateProcedureQuestionIds,
  };
}
